/**
 * Idle Detection Service
 *
 * Tracks user activity and triggers session timeout after configured idle period.
 * Prevents unlocked-computer walkaway attacks while respecting analytical workflows.
 *
 * Activity Detection:
 * - Mouse movement (throttled)
 * - Keyboard input
 * - API calls (fetch requests)
 * - Page visibility changes
 *
 * Configuration:
 * - VITE_IDLE_TIMEOUT_MINUTES (default: 120 minutes / 2 hours)
 * - Warning shown 5 minutes before timeout
 * - Automatic logout at timeout threshold
 */

import { authService } from './authService'
import config from '@/config/environments'

// Configuration
const DEFAULT_TIMEOUT_MINUTES = 120 // 2 hours - respects analytical workflows
const WARNING_BEFORE_TIMEOUT_MS = 5 * 60 * 1000 // 5 minutes warning
const THROTTLE_MS = 1000 // Throttle activity tracking to once per second

// Get configured timeout from environment
const IDLE_TIMEOUT_MS = import.meta.env.VITE_IDLE_TIMEOUT_MINUTES
  ? parseInt(import.meta.env.VITE_IDLE_TIMEOUT_MINUTES) * 60 * 1000
  : DEFAULT_TIMEOUT_MINUTES * 60 * 1000

// Event types
type IdleEventType = 'warning' | 'timeout'
type IdleEventCallback = (event: IdleEventType, idleDuration: number) => void

class IdleDetectionService {
  private lastActivityTime: number = Date.now()
  private checkInterval: ReturnType<typeof setInterval> | null = null
  private throttleTimer: ReturnType<typeof setTimeout> | null = null
  private eventListeners: IdleEventCallback[] = []
  private isInitialized: boolean = false
  private hasWarned: boolean = false
  private isInternalStaff: boolean = false
  private originalFetch: typeof window.fetch | null = null

  /**
   * Initialize idle detection and start tracking user activity.
   * Call this after successful login.
   */
  initialize(options?: { isInternalStaff?: boolean }): void {
    if (this.isInitialized) {
      console.warn('IdleDetectionService already initialized')
      return
    }

    console.log(`Initializing idle detection with ${IDLE_TIMEOUT_MS / 1000 / 60} minute timeout`)

    this.lastActivityTime = Date.now()
    this.isInitialized = true
    this.hasWarned = false
    this.isInternalStaff = options?.isInternalStaff ?? false

    // Set up activity listeners
    this.setupActivityListeners()

    // Start interval-based checking
    this.startIntervalCheck()
  }

  /**
   * Stop idle detection and clean up listeners.
   * Call this on logout.
   */
  destroy(): void {
    console.log('Destroying idle detection')

    this.isInitialized = false
    this.removeActivityListeners()
    this.stopIntervalCheck()
    this.eventListeners = []
  }

  /**
   * Register a callback for idle events.
   *
   * @param callback - Function to call when warning or timeout occurs
   * @returns Cleanup function to remove the listener
   */
  onIdleEvent(callback: IdleEventCallback): () => void {
    this.eventListeners.push(callback)

    // Return cleanup function
    return () => {
      this.eventListeners = this.eventListeners.filter(cb => cb !== callback)
    }
  }

  /**
   * Check if user is currently idle.
   * Used by token refresh logic to skip refresh for idle users.
   */
  isIdle(): boolean {
    const idleDuration = Date.now() - this.lastActivityTime
    return idleDuration >= IDLE_TIMEOUT_MS
  }

  /**
   * Get current idle duration in milliseconds.
   */
  getIdleDuration(): number {
    return Date.now() - this.lastActivityTime
  }

  /**
   * Get configured timeout duration in minutes.
   */
  getTimeoutMinutes(): number {
    return IDLE_TIMEOUT_MS / 1000 / 60
  }

  /**
   * Manually reset idle timer (e.g., when user clicks "Stay Logged In").
   */
  resetActivity(): void {
    console.log('Manual activity reset')
    this.recordActivity()
  }

  // ─────────────────────────────────────────────────────────────
  // Private Methods
  // ─────────────────────────────────────────────────────────────

  private setupActivityListeners(): void {
    // Mouse movement (throttled for performance)
    document.addEventListener('mousemove', this.handleMouseActivity)

    // Keyboard input
    document.addEventListener('keydown', this.handleActivity)

    // Page visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange)

    // Intercept fetch requests to detect API calls
    this.interceptFetch()
  }

  private removeActivityListeners(): void {
    document.removeEventListener('mousemove', this.handleMouseActivity)
    document.removeEventListener('keydown', this.handleActivity)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)

    // Restore original fetch
    if (this.originalFetch) {
      window.fetch = this.originalFetch
      this.originalFetch = null
    }
  }

  private handleActivity = (): void => {
    this.recordActivity()
  }

  private handleMouseActivity = (): void => {
    // Throttle mouse movement to avoid performance impact
    if (this.throttleTimer) {
      return
    }

    this.throttleTimer = setTimeout(() => {
      this.throttleTimer = null
      this.recordActivity()
    }, THROTTLE_MS)
  }

  private handleVisibilityChange = (): void => {
    if (!document.hidden) {
      // User returned to tab - check if they've been idle too long
      this.checkIdleStatus()
    }
  }

  private interceptFetch(): void {
    // Store original fetch for restoration on destroy
    this.originalFetch = window.fetch

    // Override fetch to detect API calls
    // CRITICAL: Use arrow function bound to window to preserve 'this' context
    window.fetch = async (...args) => {
      // Check if this is an automatic call that should not count as activity
      const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url

      // Exclude automatic operations from activity tracking:
      // - Token refresh (backend maintenance)
      // - OAuth/login flows (authentication flow)
      // - Cognito endpoints (authentication flow)
      const isAutomatic = url?.includes('/auth/refresh') ||
                          url?.includes('/auth/callback') ||
                          url?.includes('/login') ||
                          url?.includes('cognito')

      // Only count as activity if NOT automatic
      if (!isAutomatic) {
        this.recordActivity()
      }

      // Call original fetch with proper context binding
      return this.originalFetch!.call(window, ...args)
    }
  }

  private recordActivity(): void {
    if (!this.isInitialized) {
      return
    }

    this.lastActivityTime = Date.now()
    this.hasWarned = false
  }

  private startIntervalCheck(): void {
    // Check every 60 seconds for idle status
    const CHECK_INTERVAL_MS = 60 * 1000 // 1 minute

    this.checkInterval = setInterval(() => {
      this.checkIdleStatus()
    }, CHECK_INTERVAL_MS)

    // Also do immediate check
    this.checkIdleStatus()
  }

  private stopIntervalCheck(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval)
      this.checkInterval = null
    }
  }

  private checkIdleStatus(): void {
    if (!this.isInitialized) {
      return
    }

    const idleDuration = this.getIdleDuration()

    // Check for timeout (120 minutes)
    if (idleDuration >= IDLE_TIMEOUT_MS) {
      this.handleTimeout()
      return
    }

    // Check for warning (115 minutes)
    const warningThreshold = IDLE_TIMEOUT_MS - WARNING_BEFORE_TIMEOUT_MS
    if (idleDuration >= warningThreshold && !this.hasWarned) {
      this.handleWarning()
      return
    }
  }

  private handleWarning(): void {
    if (this.hasWarned) {
      return
    }

    this.hasWarned = true
    const idleDuration = this.getIdleDuration()

    console.warn(`Idle warning: User idle for ${idleDuration / 1000 / 60} minutes`)

    // Notify listeners
    this.eventListeners.forEach(callback => {
      callback('warning', idleDuration)
    })
  }

  private handleTimeout(): void {
    const idleDuration = this.getIdleDuration()

    console.warn(`Session timeout: User idle for ${idleDuration / 1000 / 60} minutes`)

    // Notify listeners (so they can show "You've been logged out" message)
    this.eventListeners.forEach(callback => {
      callback('timeout', idleDuration)
    })

    // Trigger logout with session timeout reason
    this.performTimeoutLogout(idleDuration)
  }

  private async performTimeoutLogout(idleDuration: number): Promise<void> {
    try {
      // Destroy idle detection first
      this.destroy()

      // For internal staff (rp_admin/rp_support), clear org context before logout
      // This matches LogoutPage.tsx behavior - prevents stale org on next login
      if (this.isInternalStaff) {
        console.log('SESSION_TIMEOUT: Clearing org context for internal user...')
        try {
          const clearResponse = await fetch(`${config.authApiBaseUrl}/me/orgs/clear`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          })
          if (clearResponse.ok) {
            console.log('SESSION_TIMEOUT: Org context cleared')
          } else {
            console.warn('SESSION_TIMEOUT: Clear org returned non-OK status:', clearResponse.status)
          }
        } catch (clearError) {
          console.warn('SESSION_TIMEOUT: Failed to clear org context:', clearError)
        }
      }

      // Clear sessionStorage to prevent staying logged in
      sessionStorage.removeItem('isAuthenticated')

      // Clear localStorage
      localStorage.removeItem('cognito-auth-tokens')
      localStorage.removeItem('oauth-state')

      // Build logout URL with session timeout parameters
      const idleDurationSeconds = Math.floor(idleDuration / 1000)
      const timeoutThresholdSeconds = Math.floor(IDLE_TIMEOUT_MS / 1000)

      const logoutUrl = `/auth/logout?reason=session_timeout&idle_duration=${idleDurationSeconds}&timeout_threshold=${timeoutThresholdSeconds}`

      // Call logout endpoint (authService will handle the API call and cookie clearing)
      await authService.signOut(logoutUrl)

      console.log('Session timeout logout completed')

      // Force redirect to login
      window.location.href = '/login?timeout=true'
    } catch (error) {
      console.error('Error during timeout logout:', error)

      // Best-effort: clear everything and redirect to login even if API call fails
      sessionStorage.removeItem('isAuthenticated')
      localStorage.removeItem('cognito-auth-tokens')
      localStorage.removeItem('oauth-state')
      window.location.href = '/login?timeout=true'
    }
  }
}

// Export singleton instance
export const idleDetectionService = new IdleDetectionService()
